import { Button, Flex, Input, Modal, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type React from "react";
import { useEffect, useState } from "react";
import { type EhrDirectoryRow, ehrDirectoryService } from "../../service/ehrDirectory.service";
import styles from "./members.module.scss";

const { Text } = Typography;

// Friendly labels for known EHR provider slugs; an unknown provider falls back to
// a title-cased version of its raw slug so a newly-supported EHR never renders as
// a bare code (e.g. "next_gen" -> "Next Gen").
const EHR_PROVIDER_LABELS: Record<string, string> = {
  ecw: "eClinicalWorks",
  advancedmd: "AdvancedMD",
  valant: "Valant",
  therapynotes: "TherapyNotes",
  charm: "CharmHealth",
  caretracker: "CareTracker",
  boulevard: "Boulevard",
  tebra: "Tebra",
  nextgen: "NextGen",
};

const formatEhrProvider = (provider: string): string => {
  if (!provider) return "EHR";
  return (
    EHR_PROVIDER_LABELS[provider.toLowerCase()] ??
    provider
      .split(/[_\s-]+/)
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(" ")
  );
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Found in your EHR" — lists staff discovered in the group's EHR who aren't yet
// Nudge users. Rendered below the members list. Renders nothing until the fetch
// resolves and only if at least one person was found, so there is never an empty
// section.
const EhrDirectorySection: React.FC = () => {
  const [rows, setRows] = useState<EhrDirectoryRow[]>([]);
  const [canInvite, setCanInvite] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeRow, setActiveRow] = useState<EhrDirectoryRow | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ehrDirectoryService
      .getEhrDirectory()
      .then((data) => {
        if (!cancelled) {
          setRows(data.rows ?? []);
          setCanInvite(Boolean(data.can_invite));
        }
      })
      .catch(() => {
        // Supplementary section: if the directory can't be loaded we simply omit
        // it rather than disrupt the members page with an error toast.
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openInvite = (row: EhrDirectoryRow) => {
    setActiveRow(row);
    setInviteEmail(row.email || "");
  };

  const closeInvite = () => {
    if (inviting) return;
    setActiveRow(null);
    setInviteEmail("");
  };

  const handleInvite = async () => {
    if (!activeRow) return;
    const email = inviteEmail.trim();
    if (!EMAIL_REGEX.test(email)) {
      message.error("Please enter a valid email address");
      return;
    }
    try {
      setInviting(true);
      const res = await ehrDirectoryService.inviteEhrDirectoryRow(activeRow.id, email);
      setRows((prev) =>
        prev.map((r) =>
          r.id === activeRow.id ? { ...r, invited: true, invited_at: res.invited_at, email } : r,
        ),
      );
      message.success(res.detail || "Invitation sent");
      setActiveRow(null);
      setInviteEmail("");
    } catch (error: any) {
      message.error(error?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const columns: ColumnsType<EhrDirectoryRow> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <Text>{name || "—"}</Text>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role: string) =>
        role ? <span style={{ textTransform: "capitalize" }}>{role}</span> : "—",
    },
    {
      title: "EHR",
      dataIndex: "ehr_provider",
      key: "ehr_provider",
      render: (provider: string) => <Tag color="blue">{formatEhrProvider(provider)}</Tag>,
    },
    {
      title: "NPI",
      dataIndex: "npi",
      key: "npi",
      render: (npi: string) => (npi ? <Text>{npi}</Text> : <Text type="secondary">—</Text>),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => {
        if (record.invited) return <Tag color="green">Invited</Tag>;
        if (!canInvite) return <Text type="secondary">—</Text>;
        return (
          <Button size="small" type="primary" onClick={() => openInvite(record)}>
            Invite
          </Button>
        );
      },
    },
  ];

  // Nothing discovered (or the fetch failed) → render no section at all.
  if (!loaded || rows.length === 0) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <Flex vertical gap={2} style={{ marginBottom: 16, textAlign: "left" }}>
        <Text strong style={{ fontSize: 16 }}>
          Found in your EHR
        </Text>
        <Text style={{ fontSize: 13, color: "#6B7280" }}>
          People discovered in your EHR who aren't on Nudge yet. Invite them so their patient
          schedule is ready the day they join.
        </Text>
      </Flex>
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        rowKey="id"
        className={styles.members__table}
      />
      <Modal
        title={activeRow ? `Invite ${activeRow.name || "team member"}` : "Invite"}
        open={activeRow !== null}
        onCancel={closeInvite}
        onOk={handleInvite}
        okText="Send Invitation"
        confirmLoading={inviting}
        maskClosable={!inviting}
      >
        <Text style={{ display: "block", marginBottom: 8 }}>
          Send a Nudge invitation to this person's email address.
        </Text>
        <Input
          type="email"
          value={inviteEmail}
          placeholder="name@example.com"
          onChange={(e) => setInviteEmail(e.target.value)}
          onPressEnter={handleInvite}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default EhrDirectorySection;
